import { PathsV1OpenPositionSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import {
  type OpenPosition,
  type OpenPositionSearchBody,
  type OpenPositionSearchParameters,
  useOpenPositionSearchQuery,
} from "@/common/api/open-position/use-open-position-search.query";
import { DeviceType } from "@/common/hooks/use-device";
import { createBasePredefinedTable } from "@/components/data-table/predefined/_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  BaseSorting,
  DataFetchHooks,
  PredefinedTableConfig,
  RowActionHelpers,
} from "@/components/data-table/predefined/_components/base-predefined-table.types";
import { DataTableRow, TableFeatures } from "@/components/data-table/types";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { CustomerFilter } from "@/components/filter/predefined/customer-filter";
import { OpenPositionAgingFilter } from "@/components/filter/predefined/open-position-aging-filter";
import { OpenPositionDunningLevelFilter } from "@/components/filter/predefined/open-position-dunning-level-filter";
import { OpenPositionDunningReasonFilter } from "@/components/filter/predefined/open-position-dunning-reason-filter";
import { OpenPositionTypeFilter } from "@/components/filter/predefined/open-position-type-filter";
import { RowAction } from "@/components/ui/table-row-actions";
import { FiaTrackingSource } from "@/modules/fia/fia.tracking";
import i18next from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOpenPositionSearchBody } from "../_hooks/use-open-position-search-body";
import { useDunningBlockActions } from "./_hooks/use-dunning-block-actions";
import { useOpenPositionSyncActions } from "./_hooks/use-open-position-sync-actions";
import { openPositionColumns } from "./open-position-columns";

type OpenPositionSorting = BaseSorting;
type OpenPositionExtend = PathsV1OpenPositionSearchPostParametersQueryExtend;
type OpenPositionQueryParams = OpenPositionSearchParameters & { extend?: ReadonlyArray<OpenPositionExtend> };

export type OpenPositionTableProps = BasePredefinedTableProps<
  OpenPosition,
  OpenPositionSorting,
  OpenPositionQueryParams,
  OpenPositionSearchBody,
  OpenPositionExtend
> & {
  trackingSource?: FiaTrackingSource;
};

const DEFAULT_EXTEND: OpenPositionExtend[] = [
  PathsV1OpenPositionSearchPostParametersQueryExtend.Customer,
  PathsV1OpenPositionSearchPostParametersQueryExtend.Customer_CustomerIdentifiers,
  PathsV1OpenPositionSearchPostParametersQueryExtend.Branch,
  PathsV1OpenPositionSearchPostParametersQueryExtend.DunningBlock,
  PathsV1OpenPositionSearchPostParametersQueryExtend.DunningBlockSapSync,
];

const DEFAULT_COLUMNS = [
  openPositionColumns.invoiceNumber,
  { ...openPositionColumns.type, visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen] },
  openPositionColumns.amount,
  openPositionColumns.dueDate,
  { ...openPositionColumns.dunningLevel, visibility: [DeviceType.Desktop, DeviceType.Widescreen] },
  { ...openPositionColumns.dunningReason, visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen] },
  { ...openPositionColumns.branch, visibility: [DeviceType.Widescreen] },
  { ...openPositionColumns.customer, visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen] },
  { ...openPositionColumns.customerSapId, visibility: [DeviceType.Desktop, DeviceType.Widescreen] },
];

const OPEN_POSITION_TABLE_CONFIG: PredefinedTableConfig<OpenPositionSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [{ field: "dueDate", direction: "asc" }],
  defaultTableConfig: {
    searchPlaceholder: i18next.t("fia:SearchOpenPositions"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  },
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: false,
    showRestore: false,
  },
  sortingMapping: {
    type: ["type"],
    amount: ["amount"],
    branch: ["branchName"],
    customer: ["customerName"],
    invoiceNumber: ["invoiceNumber"],
    dueDate: ["dueDate"],
    dunningLevel: ["dunningLevel"],
  },
};

const OPEN_POSITION_TABLE_QUERY_HOOKS: DataFetchHooks<OpenPosition, OpenPositionQueryParams, OpenPositionSearchBody> = {
  useSearchQuery: useOpenPositionSearchQuery,
};

const BaseOpenPositionTable = createBasePredefinedTable({
  config: OPEN_POSITION_TABLE_CONFIG,
  queryHooks: OPEN_POSITION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function OpenPositionTable(props: OpenPositionTableProps) {
  const { t } = useTranslation(["fia"]);
  const {
    name = "open-position-table",
    filters,
    body,
    extend = DEFAULT_EXTEND,
    onRowNavigate = createRowNavigatePath<OpenPosition>("/fia/open-position"),
    trackingSource = FiaTrackingSource.OpenPositions,
    ...restProps
  } = props;
  const extendWithDunningSync = useMemo(
    () =>
      Array.from(
        new Set([
          ...extend,
          PathsV1OpenPositionSearchPostParametersQueryExtend.DunningBlock,
          PathsV1OpenPositionSearchPostParametersQueryExtend.DunningBlockSapSync,
        ]),
      ),
    [extend],
  );

  const bodyWithReferenceDate = useOpenPositionSearchBody(body);
  const { buildRowActions: buildDunningBlockActions, dialog } = useDunningBlockActions(trackingSource);
  const { buildRowActions: buildSyncActions } = useOpenPositionSyncActions();

  const buildRowActions = useCallback(
    (row: DataTableRow<OpenPosition>, helpers: RowActionHelpers): RowAction[] => [
      ...buildSyncActions(row, helpers),
      ...buildDunningBlockActions(row, helpers),
    ],
    [buildSyncActions, buildDunningBlockActions],
  );

  const defaultFilters = useMemo(
    () =>
      filters ?? [
        ...(body?.customer?.length ? [] : [new CustomerFilter({ id: "customer" })]),
        ...(body?.branch?.length ? [] : [new BranchFilter({ id: "branch" })]),
        new CompanyFilter({ id: "company" }),
        new OpenPositionTypeFilter(),
        new OpenPositionAgingFilter(),
        new OpenPositionDunningLevelFilter(),
        new OpenPositionDunningReasonFilter(),
      ],
    [body?.customer?.length, body?.branch?.length, filters],
  );

  return (
    <>
      <BaseOpenPositionTable
        name={name}
        searchPlaceholder={t("fia:SearchOpenPositions")}
        filters={defaultFilters}
        body={bodyWithReferenceDate}
        extend={extendWithDunningSync}
        customRowActions={buildRowActions}
        onRowNavigate={onRowNavigate}
        {...restProps}
      />
      {dialog}
    </>
  );
}
